import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { blockTypesForPage, getBlockDef } from "@/cms/blockRegistry";
import BlockFieldsEditor from "./BlockFieldsEditor";

function moveBlock(blocks, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= blocks.length) return blocks;
  const next = [...blocks];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Ordered list of block instances for one page — add/remove/reorder
 * blocks (move-up/move-down, no dnd library installed) and edit each
 * block's fields inline via the generic BlockFieldsEditor. */
export default function BlockList({ pageKey, blocks, onChange, onUploadImage }) {
  const [openId, setOpenId] = useState(blocks[0]?.id ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const availableTypes = blockTypesForPage(pageKey);

  const updateBlockFields = (id, key, value) => {
    onChange(
      blocks.map((b) =>
        b.id === id ? { ...b, fields: { ...b.fields, [key]: value } } : b,
      ),
    );
  };

  const removeBlock = (id) => {
    onChange(blocks.filter((b) => b.id !== id));
  };

  const addBlock = (type) => {
    const def = getBlockDef(type);
    if (!def) return;
    const newBlock = {
      id: crypto.randomUUID(),
      type,
      fields: { ...def.defaultFields },
    };
    onChange([...blocks, newBlock]);
    setOpenId(newBlock.id);
    setPickerOpen(false);
  };

  return (
    <div className="space-y-3">
      {blocks.map((block, index) => {
        const def = getBlockDef(block.type);
        const isOpen = openId === block.id;
        return (
          <div
            key={block.id}
            className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3">
              <GripVertical className="h-4 w-4 text-gray-300 shrink-0" />
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : block.id)}
                className="flex-1 text-left font-semibold text-[#0B2421] text-sm"
              >
                {def?.label || block.type}
              </button>

              <button
                type="button"
                onClick={() => onChange(moveBlock(blocks, index, -1))}
                disabled={index === 0}
                className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveBlock(blocks, index, 1))}
                disabled={index === blocks.length - 1}
                className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => removeBlock(block.id)}
                className="h-8 w-8 rounded-lg border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : block.id)}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400"
              >
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>

            {isOpen && def && (
              <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                <BlockFieldsEditor
                  schema={def.fieldsSchema}
                  values={block.fields}
                  onChange={(key, value) =>
                    updateBlockFields(block.id, key, value)
                  }
                  onUploadImage={onUploadImage}
                />
              </div>
            )}
          </div>
        );
      })}

      {blocks.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-gray-400 text-sm">
          No sections yet — add one below.
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="w-full h-11 rounded-xl border border-dashed border-gray-300 text-sm font-semibold text-gray-500 hover:bg-gray-50 flex items-center justify-center gap-1.5"
        >
          <Plus className="h-4 w-4" />
          Add Section
        </button>

        {pickerOpen && (
          <div className="absolute z-10 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg p-1.5">
            {availableTypes.map(({ type, label }) => (
              <button
                key={type}
                type="button"
                onClick={() => addBlock(type)}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
